import { useEffect, useRef } from 'react';
import type { TravelMode } from '@london-life/contracts';
import type { Character } from '@london-life/game/game';
import { createRide, type Ride, type RideView } from '@london-life/game/journey/ride';
import { seedOf } from '@london-life/game/journey/path';

// The street on the way somewhere (Lagos Life's ride scene): over the map, under the bars at the top, until the
// journey ends. If the device cannot draw it smoothly it says so, and the map stays.
export function RideScreen({
  journey,
  areas,
  character,
  night,
  dataSaver,
  view,
  onUnavailable,
}: {
  journey: {
    mode: TravelMode;
    startedAt: number;
    endsAt: number;
    from: string;
    to: string;
    name: string;
    icon: string;
    // the player's own car, when the way is "car"
    car?: { paint: string; bike: boolean; driver: boolean };
  };
  areas: string[];
  character: Character;
  night: boolean;
  dataSaver: boolean;
  view: RideView;
  onUnavailable: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const ride = useRef<Ride | null>(null);
  const latest = useRef({ view, onUnavailable });
  latest.current = { view, onUnavailable };
  const key = `${journey.from}>${journey.to}@${journey.startedAt}`;
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const made = createRide(element, {
      mode: journey.mode,
      car: journey.car,
      startedAt: journey.startedAt,
      endsAt: journey.endsAt,
      destination: { name: journey.name, icon: journey.icon },
      areas,
      character,
      night,
      seed: seedOf(key),
      dataSaver,
      view: latest.current.view,
    });
    if (!made.ok) {
      latest.current.onUnavailable();
      return;
    }
    ride.current = made;
    return () => {
      made.dispose();
      ride.current = null;
    };
    // one street per journey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => ride.current?.setView(view), [view]);
  return <div className="ride-screen" ref={box} data-mode={journey.mode} />;
}
